import { findMatchRanges, searchTextBlocks, type NodeSearchResult } from '../../services/corpus/corpusSearch'
import { createTermsMatcher, isBodySearchable, type ParsedSearchQuery, type SearchTerm } from '../../services/corpus/searchQuery'
import type { CorpusNode, FlatSearchResult } from './model'

/** How well a node matches a search: an exact title first, then the start of one, aliases, and text matches. */
function scoreSearchResult(
  node: CorpusNode,
  terms: SearchTerm[],
  result: Omit<NodeSearchResult, 'score'>,
): number {
  const name = node.name.toLocaleLowerCase()
  const aliases = (node.aliases || []).map(alias => alias.toLocaleLowerCase())
  let score = 0
  if (result.titleRanges.length) {
    for (const term of terms) {
      const text = term.text.toLocaleLowerCase()
      if (name === text) score += 1000
      else if (name.startsWith(text)) score += 700
      else if (name.includes(text)) score += 500
    }
    score = Math.max(score, 500)
  }
  if (result.aliasMatches.length) {
    score += terms.some(term => aliases.includes(term.text.toLocaleLowerCase())) ? 400 : 300
  }
  // Diminishing returns: 1 → 10, 10 → 40, 100 → 80.
  if (result.bodyCount) score += Math.round(Math.log2(result.bodyCount + 1) * 12)
  return score
}

function testText(matcher: RegExp, text: string): boolean {
  matcher.lastIndex = 0
  return matcher.test(text)
}

export interface SearchContext {
  /** Page id → text blocks, loaded while a search runs. */
  index: Map<string, string[]>
  /** Matches the query's terms in document text, when it searches text. */
  bodyMatcher: RegExp | null
  /** Where the search starts. */
  rootNodes: CorpusNode[]
  /** The nodes above rootNodes, which still count for desk: and under: filters. */
  ancestors: CorpusNode[]
  /** A page scope covers that page alone, not the notes beneath it. */
  pageOnly: boolean
  /** Read only when a filter asks, as before: the open page. */
  activePageId: () => string | null
}

/** Nodes under the search root that match the parsed query, keyed by id. */
export function matchSearch(parsed: ParsedSearchQuery, context: SearchContext): Map<string, NodeSearchResult> {
  const results = new Map<string, NodeSearchResult>()
  const { index, bodyMatcher, pageOnly } = context

  const { fields } = parsed
  const titleMatcher = createTermsMatcher(parsed.terms)
  const compile = (term: SearchTerm) => ({
    matcher: createTermsMatcher([term]),
    body: fields.has('body') && isBodySearchable(term),
  })
  const required = parsed.terms.map(compile)
  const excluded = parsed.excluded.map(compile)

  // 'hit' | 'miss' | 'pending' (the answer depends on text not loaded yet).
  function lookup(
    node: CorpusNode,
    blocks: string[] | undefined,
    term: { matcher: RegExp | null; body: boolean },
  ): 'hit' | 'miss' | 'pending' {
    const { matcher } = term
    if (!matcher) return 'miss'
    if (fields.has('title') && testText(matcher, node.name)) return 'hit'
    if (fields.has('alias') && (node.aliases || []).some(alias => testText(matcher, alias))) return 'hit'
    if (!term.body || node.subtype !== 'page') return 'miss'
    if (!blocks) return 'pending'
    return blocks.some(block => testText(matcher, block)) ? 'hit' : 'miss'
  }

  function matches(node: CorpusNode, ancestors: CorpusNode[], blocks: string[] | undefined): boolean {
    for (const term of required) {
      if (lookup(node, blocks, term) !== 'hit') return false
    }
    for (const term of excluded) {
      if (lookup(node, blocks, term) !== 'miss') return false
    }
    for (const filter of parsed.filters) {
      if (filter.needsBody && node.subtype === 'page' && !blocks) return false
      if (!filter.test({ node, ancestors, blocks, activePageId: context.activePageId() })) return false
    }
    return true
  }

  function visit(nodes: CorpusNode[], ancestors: CorpusNode[]) {
    for (const node of nodes) {
      const blocks = node.subtype === 'page' ? index.get(node.id) : undefined
      if (matches(node, ancestors, blocks)) {
        const titleRanges = fields.has('title') && titleMatcher ? findMatchRanges(node.name, titleMatcher) : []
        const aliasMatches = fields.has('alias') && titleMatcher
          ? (node.aliases || [])
            .map(alias => ({ alias, ranges: findMatchRanges(alias, titleMatcher) }))
            .filter(match => match.ranges.length)
          : []
        const body = bodyMatcher && blocks ? searchTextBlocks(blocks, bodyMatcher) : { count: 0, snippets: [] }
        const partial = { titleRanges, aliasMatches, bodyCount: body.count, snippets: body.snippets }
        results.set(node.id, { ...partial, score: scoreSearchResult(node, parsed.terms, partial) })
      }
      if (!pageOnly && node.children?.length) visit(node.children, [...ancestors, node])
    }
  }

  visit(context.rootNodes, context.ancestors)
  return results
}

/** The tree cut down to matches and the folders holding them; matched branches open unless collapsed for this search. */
export function filterSearchTree(rootNodes: CorpusNode[], results: Map<string, NodeSearchResult>, collapsed: Set<string>): CorpusNode[] {

  function filterNodes(nodes: CorpusNode[]): CorpusNode[] {
    const result: CorpusNode[] = []
    for (const node of nodes) {
      const matchesSelf = results.has(node.id)
      let filteredChildren: CorpusNode[] = []
      if (node.children) {
        filteredChildren = filterNodes(node.children)
      }
      if (matchesSelf || filteredChildren.length > 0) {
        result.push({
          ...node,
          // Matched branches start expanded. Collapsing one is remembered
          // only for this search, so the real tree state is never changed.
          expanded: !collapsed.has(node.id),
          children: node.children ? filteredChildren : undefined,
        })
      }
    }
    return result
  }

  return filterNodes(rootNodes)
}

/** Matches as a single list, best first, each with the folders above it. */
export function flattenSearchResults(rootNodes: CorpusNode[], results: Map<string, NodeSearchResult>): FlatSearchResult[] {
  const flat: FlatSearchResult[] = []
  if (!results.size) return flat

  function visit(nodes: CorpusNode[], path: string[]) {
    for (const node of nodes) {
      const result = results.get(node.id)
      if (result) flat.push({ node, path, result })
      if (node.children?.length) visit(node.children, [...path, node.name])
    }
  }

  visit(rootNodes, [])
  // Stable sort: equally ranked results keep manuscript order.
  return flat.sort((a, b) => b.result.score - a.result.score)
}
